/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  defaultAboveGradeWall,
  defaultAppliance,
  defaultBasementFloor,
  defaultBelowGradeWall,
  defaultCeilingBelowAttic,
  defaultCeilingBelowRoof,
  defaultCeilingOnExposedBeams,
  defaultDoor,
  defaultFloorOverExposedSpace,
  defaultFloorOverUnconditionedSpace,
  defaultGlass,
  defaultSkylight,
  defaultSlabFloor
} from 'data/sizing-calculator-defaults';
import {
  AccessoryProductType,
  AirHandlerProductType,
  AirHandlerTypeType,
  ApplianceItemType,
  AttributeType,
  CondenserProductType,
  DuctingComponentProductType,
  FenestrationItemType,
  ItemType,
  OversizeRangeType,
  RestockDataType,
  SizingBandsSettingType,
  SizingItemsType,
  StructureTypeType,
  TextKeyType,
  ValidItemsType,
  ValidSizingCalculatorAttributesType
} from 'data/types';

export type DefaultCategoryType =
  | 'fenestration'
  | 'walls'
  | 'ceilings'
  | 'floors';

export interface DataState {
  items: ItemType[];
  attributes: AttributeType[];
  applianceItems: ApplianceItemType[];
  fenestration: FenestrationItemType[];
  structureTypes: StructureTypeType[];
  validItems: ValidItemsType[];
  airHandlerProducts: AirHandlerProductType[];
  condenserProducts: CondenserProductType[];
  lineSetCoverProducts: AccessoryProductType[];
  ductingKitProducts: AccessoryProductType[];
  ductingComponents: DuctingComponentProductType[];
  airHandlerTypes: AirHandlerTypeType[];
  oversizeRanges: OversizeRangeType[];
  supplementalHeatingWeight: number;
  supplementalCoolingWeight: number;
  supplementalHeatingWeightLoaded: boolean;
  ccbCapacityBandLowerBound: number;
  ccbCapacityBandUpperMultiplier: number;
  // Per-equipment-type sizing bands (AHA-6796). null = not hydrated or
  // absent/invalid in the settings response → the legacy uniform CCB
  // multipliers above stay the live fallback.
  sizingBands: SizingBandsSettingType | null;
  restockData: RestockDataType | null;
  BTULimits: number[][];
  validAirHandlerCodes: string[];
  maxDuctlessZones: number;
  criticalDataError: boolean;
  nonCriticalDataError: boolean;
  validSizingAttributes: ValidSizingCalculatorAttributesType;
  defaultSizingItems: SizingItemsType;
  defaultSizingItemsVersion: number;
  insulationLevelSettings: Record<
    string,
    Record<number, { primaryValue: string; secondaryValue: string | null }>
  > | null;
  textKeys: Record<string, string>;
  modifiedDefaultCategories: Record<DefaultCategoryType, boolean>;
}

export const defaultDataState: DataState = {
  items: [],
  attributes: [],
  applianceItems: [],
  fenestration: [],
  structureTypes: [],
  validItems: [],
  airHandlerProducts: [],
  condenserProducts: [],
  lineSetCoverProducts: [],
  ductingKitProducts: [],
  ductingComponents: [],
  airHandlerTypes: [],
  oversizeRanges: [],
  supplementalHeatingWeight: 0.5,
  supplementalCoolingWeight: 0.5,
  supplementalHeatingWeightLoaded: false,
  // Defaults mirror the migration seed; overridden by the settings fetch.
  ccbCapacityBandLowerBound: 1.0,
  ccbCapacityBandUpperMultiplier: 1.5,
  sizingBands: null,
  restockData: null,
  BTULimits: [],
  validAirHandlerCodes: [],
  maxDuctlessZones: 5,
  criticalDataError: false,
  nonCriticalDataError: false,
  validSizingAttributes: {
    aboveGradeWalls: [],
    belowGradeWalls: [],
    ceilingsBelowRoof: [],
    ceilingsBelowAttic: [],
    floorsOverUnconditionedSpace: [],
    floorsOverExposedSpace: [],
    basementFloors: [],
    slabFloors: [],
    verticalGlass: [],
    skylights: [],
    solidDoors: [],
    appliances: []
  },
  defaultSizingItems: {
    aboveGradeWall: defaultAboveGradeWall,
    belowGradeWall: defaultBelowGradeWall,
    ceilingBelowRoofJoists: defaultCeilingBelowRoof,
    ceilingOnExposedBeams: defaultCeilingOnExposedBeams,
    ceilingBelowAttic: defaultCeilingBelowAttic,
    floorOverUnconditionedSpace: defaultFloorOverUnconditionedSpace,
    floorOverExposedSpace: defaultFloorOverExposedSpace,
    slabFloor: defaultSlabFloor,
    basementFloor: defaultBasementFloor,
    skylight: defaultSkylight,
    solidDoor: defaultDoor,
    verticalGlass: defaultGlass,
    appliance: defaultAppliance
  },
  defaultSizingItemsVersion: 0,
  insulationLevelSettings: null,
  textKeys: {},
  modifiedDefaultCategories: {
    fenestration: false,
    walls: false,
    ceilings: false,
    floors: false
  }
};

export const dataSlice = createSlice({
  name: 'data',
  initialState: defaultDataState,
  reducers: {
    setItems: (state, action: PayloadAction<ItemType[]>) => {
      state.items = action.payload;
    },
    setAttributes: (state, action: PayloadAction<AttributeType[]>) => {
      state.attributes = action.payload;
    },
    setApplianceItems: (state, action: PayloadAction<ApplianceItemType[]>) => {
      state.applianceItems = action.payload;
    },
    setFenestration: (state, action: PayloadAction<FenestrationItemType[]>) => {
      state.fenestration = action.payload;
    },
    setStructureTypes: (state, action: PayloadAction<StructureTypeType[]>) => {
      state.structureTypes = action.payload;
    },
    setValidItems: (state, action: PayloadAction<ValidItemsType[]>) => {
      state.validItems = action.payload;
    },
    setAirHandlerProducts: (
      state,
      action: PayloadAction<AirHandlerProductType[]>
    ) => {
      state.airHandlerProducts = action.payload;
    },
    setCondenserProducts: (
      state,
      action: PayloadAction<CondenserProductType[]>
    ) => {
      state.condenserProducts = action.payload;
    },
    setLineSetCoverProducts: (
      state,
      action: PayloadAction<AccessoryProductType[]>
    ) => {
      state.lineSetCoverProducts = action.payload;
    },
    setDuctingKitProducts: (
      state,
      action: PayloadAction<AccessoryProductType[]>
    ) => {
      state.ductingKitProducts = action.payload;
    },
    setDuctingComponents: (
      state,
      action: PayloadAction<DuctingComponentProductType[]>
    ) => {
      state.ductingComponents = action.payload;
    },
    setAirHandlerTypes: (
      state,
      action: PayloadAction<AirHandlerTypeType[]>
    ) => {
      state.airHandlerTypes = action.payload;
    },
    setOversizeRanges: (state, action: PayloadAction<OversizeRangeType[]>) => {
      state.oversizeRanges = action.payload;
    },
    setSupplementalHeatingWeight: (state, action: PayloadAction<number>) => {
      state.supplementalHeatingWeight = action.payload;
    },
    setSupplementalCoolingWeight: (state, action: PayloadAction<number>) => {
      state.supplementalCoolingWeight = action.payload;
    },
    setSupplementalHeatingWeightLoaded: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.supplementalHeatingWeightLoaded = action.payload;
    },
    setCcbCapacityBandSettings: (
      state,
      action: PayloadAction<{ lowerBound: number; upperMultiplier: number }>
    ) => {
      state.ccbCapacityBandLowerBound = action.payload.lowerBound;
      state.ccbCapacityBandUpperMultiplier = action.payload.upperMultiplier;
    },
    setSizingBands: (
      state,
      action: PayloadAction<SizingBandsSettingType | null>
    ) => {
      state.sizingBands = action.payload;
    },
    setRestockData: (state, action: PayloadAction<RestockDataType | null>) => {
      state.restockData = action.payload;
    },
    setBTULimits: (state, action: PayloadAction<number[][]>) => {
      state.BTULimits = action.payload;
    },
    setValidAirHandlerCodes: (state, action: PayloadAction<string[]>) => {
      state.validAirHandlerCodes = action.payload;
    },
    setMaxDuctlessZones: (state, action: PayloadAction<number>) => {
      state.maxDuctlessZones = action.payload;
    },
    setCriticalDataError: (state, action: PayloadAction<boolean>) => {
      state.criticalDataError = action.payload;
    },
    setNonCriticalDataError: (state, action: PayloadAction<boolean>) => {
      state.nonCriticalDataError = action.payload;
    },
    setValidSizingAttributes: (
      state,
      action: PayloadAction<ValidSizingCalculatorAttributesType>
    ) => {
      state.validSizingAttributes = action.payload;
    },
    setDefaultSizingItems: (
      state,
      action: PayloadAction<Partial<SizingItemsType>>
    ) => {
      state.defaultSizingItems = {
        ...state.defaultSizingItems,
        ...action.payload
      };
      state.defaultSizingItemsVersion += 1;
    },
    hydrateDefaultSizingItems: (
      state,
      action: PayloadAction<Partial<SizingItemsType>>
    ) => {
      state.defaultSizingItems = {
        ...state.defaultSizingItems,
        ...action.payload
      };
    },
    updateDefaultSizingItemCategory: <K extends keyof SizingItemsType>(
      state: DataState,
      action: PayloadAction<{
        category: K;
        updates: Partial<SizingItemsType[K]>;
      }>
    ) => {
      const { category, updates } = action.payload;
      state.defaultSizingItems[category] = {
        ...state.defaultSizingItems[category],
        ...updates
      };
      state.defaultSizingItemsVersion += 1;
    },
    markDefaultCategoryModified: (
      state,
      action: PayloadAction<DefaultCategoryType>
    ) => {
      state.modifiedDefaultCategories[action.payload] = true;
    },
    setInsulationLevelSettings: (
      state,
      action: PayloadAction<
        Record<
          string,
          Record<
            number,
            { primaryValue: string; secondaryValue: string | null }
          >
        >
      >
    ) => {
      state.insulationLevelSettings = action.payload;
    },
    setTextKeys: (state, action: PayloadAction<TextKeyType[]>) => {
      state.textKeys = Object.fromEntries(
        action.payload.map((tk) => [tk.code, tk.value])
      );
    },
    setModifiedDefaultCategories: (
      state,
      action: PayloadAction<Record<DefaultCategoryType, boolean>>
    ) => {
      state.modifiedDefaultCategories = action.payload;
    }
  }
});

export const {
  setItems,
  setAttributes,
  setApplianceItems,
  setFenestration,
  setStructureTypes,
  setValidItems,
  setAirHandlerProducts,
  setCondenserProducts,
  setLineSetCoverProducts,
  setDuctingKitProducts,
  setDuctingComponents,
  setAirHandlerTypes,
  setOversizeRanges,
  setSupplementalHeatingWeight,
  setSupplementalCoolingWeight,
  setSupplementalHeatingWeightLoaded,
  setCcbCapacityBandSettings,
  setSizingBands,
  setRestockData,
  setBTULimits,
  setValidAirHandlerCodes,
  setMaxDuctlessZones,
  setCriticalDataError,
  setNonCriticalDataError,
  setValidSizingAttributes,
  setInsulationLevelSettings,
  setTextKeys,
  setDefaultSizingItems,
  hydrateDefaultSizingItems,
  updateDefaultSizingItemCategory,
  markDefaultCategoryModified,
  setModifiedDefaultCategories
} = dataSlice.actions;

export default dataSlice.reducer;
